import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { axiosInstance } from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { useJednotkaQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import AppearanceMenu from '../components/AppearanceMenu';

const SetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const { data: jednotka } = useJednotkaQuery();

    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (!token) {
            setValidating(false);
            setTokenError('Chybí token v odkazu');
            return;
        }

        axiosInstance.get(`/api/auth/reset-password/validate`, { params: { token } })
            .then((response) => {
                if (response.data?.valid) {
                    setTokenValid(true);
                } else {
                    setTokenError(response.data?.error || 'Neplatný odkaz');
                }
            })
            .catch(() => {
                setTokenError('Odkaz je neplatný nebo vypršel');
            })
            .finally(() => {
                setValidating(false);
            });
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError('');

        if (password.length < 8) {
            setSubmitError('Heslo musí mít alespoň 8 znaků');
            return;
        }
        if (password !== confirmPassword) {
            setSubmitError('Hesla se neshodují');
            return;
        }

        setSubmitting(true);
        try {
            await axiosInstance.post('/api/auth/reset-password/complete', {
                token,
                newPassword: password,
            });
            setSuccess(true);
        } catch (err: any) {
            const errorMsg = err?.response?.data?.error || 'Nastala chyba při nastavování hesla';
            setSubmitError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const passwordsMatch = password.length > 0 && password === confirmPassword;
    const canSubmit = password.length >= 8 && passwordsMatch && !submitting;

    return (
        <Container maxWidth="xs" sx={{ mt: 8 }}>
            <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
                <AppearanceMenu />
            </Box>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Nastavení hesla
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {jednotka?.nazev || 'Hasiči Online'}
                    </Typography>
                </Box>

                {validating && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                )}

                {!validating && !tokenValid && !success && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {tokenError}
                    </Alert>
                )}

                {!validating && tokenValid && !success && (
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Nové heslo"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            margin="normal"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={submitting}
                            autoComplete="new-password"
                            autoFocus
                            helperText="Minimálně 8 znaků"
                            error={password.length > 0 && password.length < 8}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(prev => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Potvrzení hesla"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            margin="normal"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={submitting}
                            autoComplete="new-password"
                            error={confirmPassword.length > 0 && !passwordsMatch}
                            helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Hesla se neshodují' : ''}
                        />

                        {submitError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {submitError}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={!canSubmit}
                            sx={{ mt: 3, mb: 2, height: 48 }}
                        >
                            {submitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Nastavit heslo'
                            )}
                        </Button>
                    </Box>
                )}

                {success && (
                    <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                        <CheckCircleOutline color="success" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Heslo bylo úspěšně nastaveno
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Nyní se můžete přihlásit s novým heslem.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/login')}
                            fullWidth
                        >
                            Přejít na přihlášení
                        </Button>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default SetPasswordPage;
